import { ec2InstancesLabel, isVolumeManaged } from '../../lib/classification';
import type { ExplorationVolume, ViewMode } from '../../types/exploration';

export interface TreemapVolumeItem {
  kind: 'volume';
  value: number;
  volume: ExplorationVolume;
}

export interface TreemapSmallAggItem {
  kind: 'small-agg';
  value: number;
  smallVols: ExplorationVolume[];
  smallTotal: number;
}

export type TreemapItem = TreemapVolumeItem | TreemapSmallAggItem;

export interface TreemapCard {
  name: string;
  kind: string;
  volumes: ExplorationVolume[];
  totalSize: number;
  totalCount: number;
  managedSize: number;
  supportedSize: number;
  statusCounts: Map<string, number>;
  _tmItems?: TreemapItem[];
  contentW?: number;
  contentH?: number;
  boxW?: number;
  boxH?: number;
  relX?: number;
  relY?: number;
}

export interface TreemapWrapper {
  name: string;
  kind: string;
  cards: TreemapCard[];
  totalSize: number;
  totalCount: number;
  _accountName?: string;
  _accountHeader?: string;
  _accountSize?: number;
  _accountCount?: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

interface TreemapWrapperBuilder extends Omit<TreemapWrapper, 'cards'> {
  cards: Map<string, TreemapCard>;
}

export function buildTreemapWrappers(
  volumes: ExplorationVolume[],
  mode: 'region' | 'hierarchy',
  view: ViewMode
): TreemapWrapper[] {
  function makeCard(name: string, kind: string): TreemapCard {
    return {
      name,
      kind,
      volumes: [],
      totalSize: 0,
      totalCount: 0,
      managedSize: 0,
      supportedSize: 0,
      statusCounts: new Map(),
    };
  }

  function addVolToCard(c: TreemapCard, v: ExplorationVolume) {
    c.volumes.push(v);
    c.totalSize += v.size || 0;
    c.totalCount++;
    if (isVolumeManaged(v)) c.managedSize += v.size || 0;
    if (v.datafiable) c.supportedSize += v.size || 0;
    const key = view === 'activation' ? v.activationStatus : v.supportStatus;
    c.statusCounts.set(key, (c.statusCounts.get(key) || 0) + 1);
  }

  const wrappers = new Map<string, TreemapWrapperBuilder>();
  function ensureWrap(key: string, kind: string) {
    if (!wrappers.has(key)) {
      wrappers.set(key, { name: key, kind, cards: new Map(), totalSize: 0, totalCount: 0 });
    }
    return wrappers.get(key)!;
  }
  function ensureCard(w: { cards: Map<string, TreemapCard> }, cardKey: string, cardKind: string) {
    if (!w.cards.has(cardKey)) w.cards.set(cardKey, makeCard(cardKey, cardKind));
    return w.cards.get(cardKey)!;
  }

  volumes.forEach((v) => {
    if (mode === 'region') {
      const wrapKey = v.cluster ? 'Kubernetes' : ec2InstancesLabel;
      const w = ensureWrap(wrapKey, v.cluster ? 'k8s' : 'ec2');
      const cardKey = v.region || '(unknown)';
      addVolToCard(ensureCard(w, cardKey, 'region'), v);
    } else {
      let wrapKey: string;
      let wrapKind: string;
      let cardKey: string;
      let cardKind: string;
      if (v.cluster) {
        wrapKey = v.cluster;
        wrapKind = 'k8s';
        cardKey = v.nodeGroup || '(No Group)';
        cardKind = 'nodeGroup';
      } else if (v.instanceId) {
        wrapKey = ec2InstancesLabel;
        wrapKind = 'ec2';
        cardKey = ec2InstancesLabel;
        cardKind = 'standalone';
      } else {
        wrapKey = 'Unattached';
        wrapKind = 'unattached';
        cardKey = 'Unattached';
        cardKind = 'unattached';
      }
      addVolToCard(ensureCard(ensureWrap(wrapKey, wrapKind), cardKey, cardKind), v);
    }
  });

  return [...wrappers.values()]
    .map((w) => {
      const cards = [...w.cards.values()];
      cards.forEach((c) => c.volumes.sort((a, b) => (b.size || 0) - (a.size || 0)));
      cards.sort((a, b) => b.totalSize - a.totalSize);
      return {
        ...w,
        cards,
        totalSize: cards.reduce((s, c) => s + c.totalSize, 0),
        totalCount: cards.reduce((s, c) => s + c.totalCount, 0),
      };
    })
    .sort((a, b) => b.totalSize - a.totalSize);
}
